import { useState, useEffect, useRef, type RefObject } from 'react'

interface CanvasDimensions {
  width: number
  height: number
}

interface UseCanvasDimensionsResult {
  containerRef: RefObject<HTMLDivElement>
  dimensions: CanvasDimensions
}

/**
 * コンテナサイズに応じてキャンバスの寸法を動的に計算するフック。
 * ResizeObserver でサイズ変更を監視し、デバイスピクセル比を考慮する。
 */
export function useCanvasDimensions(
  initialWidth = 500,
  initialHeight = 500
): UseCanvasDimensionsResult {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState<CanvasDimensions>({
    width: initialWidth,
    height: initialHeight,
  })

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateDimensions = () => {
      const rect = container.getBoundingClientRect()
      if (rect.width === 0 || rect.height === 0) return

      const dpr = window.devicePixelRatio || 1
      const newWidth = Math.floor(rect.width * dpr)
      const newHeight = Math.floor(rect.height * dpr)

      setDimensions({ width: newWidth, height: newHeight })
    }

    const initialTimeout = setTimeout(updateDimensions, 0)

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateDimensions)
    })
    resizeObserver.observe(container)

    window.addEventListener('resize', updateDimensions)

    return () => {
      clearTimeout(initialTimeout)
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateDimensions)
    }
  }, [])

  return {
    containerRef,
    dimensions,
  }
}
